import dayjs from 'dayjs';
import { RegisterOptions } from 'react-hook-form';

import { FrequencyCode, WidgetInputs } from '../@types/types';

export const frequencies: {
	[key: string]: { code: FrequencyCode; text: string; order: number; name: string };
} = {
	OneMinute: {
		name: 'OneMinute',
		code: FrequencyCode.OneMinute,
		text: 'Every minute',
		order: 0,
	},
	FiveMinutes: {
		name: 'FiveMinutes',
		code: FrequencyCode.FiveMinutes,
		text: 'Every 5 minutes',
		order: 1,
	},
	FourHours: {
		name: 'FourHours',
		code: FrequencyCode.FourHours,
		text: 'Every 4 hours',
		order: 2,
	},
	Weekly: {
		name: 'Weekly',
		code: FrequencyCode.Weekly,
		text: 'Every week',
		order: 3,
	},
	Fortnightly: {
		name: 'Fortnightly',
		code: FrequencyCode.Fortnightly,
		text: 'Every 2 weeks',
		order: 4,
	},
	FirstAndFifteenth: {
		name: 'FirstAndFifteenth',
		code: FrequencyCode.FirstAndFifteenth,
		text: '1st & 15th monthly',
		order: 5,
	},
	Monthly: {
		name: 'Monthly',
		code: FrequencyCode.Monthly,
		text: 'Every month',
		order: 6,
	},
	Yearly: {
		name: 'Yearly',
		code: FrequencyCode.Yearly,
		text: 'Every year',
		order: 9,
	},
	SemiYearly: {
		name: 'SemiYearly',
		code: FrequencyCode.SemiYearly,
		text: 'Every 6 months',
		order: 8,
	},
	Quarterly: {
		name: 'Quarterly',
		code: FrequencyCode.Quarterly,
		text: 'Every 3 months',
		order: 7,
	},
};

export const defaultFrequency = frequencies.Monthly.name;

export const enum GiftType {
	ONE_TIME,
	RECURRING,
}

export enum GivingType {
	CARD_OR_ACH,
	CRYPTOCURRENCY,
	STOCK,
}

export const regexValidationLettersOnly = '^\\D*$';
export const regexValidationNumbersOnly = '^[0-9.]*$';

export const PaymentLabels = {
	DONATE: {
		ActionLowerCase: 'donation',
		ActionPluralLowerCase: 'donations',
		ActionPluralSentenceCase: 'Donations',
		ActionSentenceCase: 'Donation',
		GiftEntryFeatureName: 'Donation Entry',
		NonCashNounLowerCase: 'non-cash donation',
		NonCashNounSentenceCase: 'Non-cash donation',
		NonCashPluralLowerCase: 'non-cash donations',
		NonCashPluralSentenceCase: 'Non-cash donations',
		NounLowerCase: 'donation',
		NounPluralLowerCase: 'donations',
		NounPluralSentenceCase: 'Donations',
		NounSentenceCase: 'Donation',
		PayerLowerCase: 'donor',
		PayerPluralLowerCase: 'donors',
		PayerPluralSentenceCase: 'Donors',
		PayerSentenceCase: 'Donor',
		TextGivingFeatureName: 'Text Donations',
		VerbAppliedLowerCase: 'donate to',
		VerbAppliedSentenceCase: 'Donate to',
		VerbLowerCase: 'donate',
		VerbPastTenseLowerCase: 'donated',
		VerbPastTenseSentenceCase: 'Donated',
		VerbPresentTenseLowerCase: 'donating',
		VerbPresentTenseSentenceCase: 'Donating',
		VerbSentenceCase: 'Donate',
	},
	GIVE: {
		ActionLowerCase: 'giving',
		ActionPluralLowerCase: 'giving',
		ActionPluralSentenceCase: 'Giving',
		ActionSentenceCase: 'Giving',
		GiftEntryFeatureName: 'Virtual Terminal',
		NonCashNounLowerCase: 'non-cash gift',
		NonCashNounSentenceCase: 'Non-cash gift',
		NonCashPluralLowerCase: 'non-cash gifts',
		NonCashPluralSentenceCase: 'Non-cash gifts',
		NounLowerCase: 'gift',
		NounPluralLowerCase: 'gifts',
		NounPluralSentenceCase: 'Gifts',
		NounSentenceCase: 'Gift',
		PayerLowerCase: 'giver',
		PayerPluralLowerCase: 'givers',
		PayerPluralSentenceCase: 'Givers',
		PayerSentenceCase: 'Giver',
		TextGivingFeatureName: 'Text Giving',
		VerbAppliedLowerCase: 'give to',
		VerbAppliedSentenceCase: 'Give to',
		VerbLowerCase: 'give',
		VerbPastTenseLowerCase: 'given',
		VerbPastTenseSentenceCase: 'Given',
		VerbPresentTenseLowerCase: 'giving',
		VerbPresentTenseSentenceCase: 'Giving',
		VerbSentenceCase: 'Give',
	},
	PAY: {
		ActionLowerCase: 'payment',
		ActionPluralLowerCase: 'payments',
		ActionPluralSentenceCase: 'Payments',
		ActionSentenceCase: 'Payment',
		GiftEntryFeatureName: 'Payment Entry',
		NonCashNounLowerCase: 'non-cash gift',
		NonCashNounSentenceCase: 'Non-cash gift',
		NonCashPluralLowerCase: 'non-cash gifts',
		NonCashPluralSentenceCase: 'Non-cash gifts',
		NounLowerCase: 'payment',
		NounPluralLowerCase: 'payments',
		NounPluralSentenceCase: 'Payments',
		NounSentenceCase: 'Payment',
		PayerLowerCase: 'payer',
		PayerPluralLowerCase: 'payers',
		PayerPluralSentenceCase: 'Payers',
		PayerSentenceCase: 'Payer',
		TextGivingFeatureName: 'Text Payments',
		VerbAppliedLowerCase: 'pay',
		VerbAppliedSentenceCase: 'Pay',
		VerbLowerCase: 'pay',
		VerbPastTenseLowerCase: 'paid',
		VerbPastTenseSentenceCase: 'Paid',
		VerbPresentTenseLowerCase: 'paying',
		VerbPresentTenseSentenceCase: 'Paying',
		VerbSentenceCase: 'Pay',
	},
};

export const PCGLQueryKeys = {
	Source: 'src',
	SignedConfig: 'wgc',
	Recurrence: 'r',
	UserFirstName: 'ufn',
	UserLastName: 'uln',
	UserEmail: 'ue',
	UserPhone: 'up',
	GiftDate: 'gd',
	RecurringStartDate: 'rsd',
	RecurringEndType: 'ret',
	RecurringEndDate: 'red',
	RecurringEndOccurence: 'reo',
	RecurringSelectorIsVisible: 'rcv',
	RedirectUrl: 'ru',
	ExternalReference: 'sr',
	FundKeyOrName: 'fnd',
	Language: 'lang',
	Notes: 'nt',
	AdditionalData: 'ad',
	Amount: 'a',
	AmountIsLocked: 'al',
	FundVisibility: 'fndv',
	RecurringSuggestion: 'rs',
	RecurringScheduleMigrationReturnUrlKey: 'rsm',
	AttemptSkipGivePage: 'askgp',
	MultiFunds: 'mf',
};

export const OnetimeGiving = 'one';
export const embeddedSource = 'ppemb';
export const dateInputFormat = 'YYYY-MM-DD';

export const defaultFieldOptions: Partial<Record<keyof typeof PCGLQueryKeys, RegisterOptions<WidgetInputs>>> = {
	Amount: {
		required: true,
	},
	RecurringEndOccurence: {
		max: 1000,
		min: 1,
	},
	// FeatureFlag(MultiFunds) will make FundKeyOrName obsolete
	FundKeyOrName: {
		required: true,
	},
};

export const dateFieldOptions = {
	type: 'date',
	min: dayjs(new Date()).startOf('day').format(dateInputFormat),
	minEnd: dayjs(new Date()).startOf('day').add(1, 'day').format(dateInputFormat),
};

export const creditCardCheckRegex = /([2-6](\d[ \\-]*){11,18})/i;

export const WidgetId = 'EmbeddedWidget';

export const WidgetErrors = {
	merchantConfigFailed: 'MerchantConfigFetchFailed',
	failedToRender: 'FailedToRender',
	boundaryError: 'ErrorBoundary',
	applePaySDKCheckFailed: 'ApplePaySDKCheckFailed',
	applePaySheetClosedWithError: 'ApplePaySheetClosedWithError',
	applePaymentRequestMissing: 'ApplePaymentRequestMissing',
	applePaySessionError: 'ApplePaySessionError',
	applePayValidateMerchantError: 'ApplePayValidateMerchantError',
	failedToLoad: 'FailedToLoad',
};

export const WidgetActions = {
	applePaySheetClosed: 'ApplePaySheetClosed',
	applePaySheetOpened: 'ApplePaySheetOpened',
	successPage: 'SuccessPageViewed',
	successfullyLoaded: 'WidgetLoaded',
	cryptoGivingClicked: 'CryptoGivingLinkClicked',
	stockGivingClicked: 'StockGivingLinkClicked',
};

export const givingAmountMax = 100000;
